/**
 * Phase 639 — First-Run Wizard
 *
 * 4-step onboarding modal that fires for academy owners whose academy
 * has fewer than 3 students AND who haven't dismissed the wizard.
 * Kills the "0 everything" empty-state cognitive overload by giving
 * new owners a single clear starting path:
 *
 *   1. Confirm your sport (already picked but let them re-confirm)
 *   2. Add 3 students in under 60 seconds
 *   3. Set your monthly fee amount
 *   4. Share the QR poster with parents on WhatsApp
 *
 * Progress is persisted in localStorage AND on the academy document
 * (`first_run_completed_at`) so a dismissal on one device sticks
 * everywhere.
 */
import { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { CheckCircle2, ArrowRight, ArrowLeft, Sparkles, Users, IndianRupee, Share2, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const STORAGE_KEY = 'outplay_first_run_dismissed';

export default function FirstRunWizard({ academy, students, onDismiss }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [sport, setSport] = useState('');
  const [studentsAdded, setStudentsAdded] = useState(0);
  const [studentForm, setStudentForm] = useState({ name: '', parent_phone: '' });
  const [feeAmount, setFeeAmount] = useState('');
  const [sharedQr, setSharedQr] = useState(false);

  const totalSteps = 4;
  const progressPct = Math.round((step / totalSteps) * 100);

  useEffect(() => {
    // Show the wizard when: (a) not previously dismissed, (b) academy
    // has < 3 students, (c) first_run_completed_at is not set on the
    // academy doc. All three combined keeps the wizard silent for
    // established academies.
    if (!academy) return;
    if (academy.first_run_completed_at) return;
    if (localStorage.getItem(STORAGE_KEY) === 'true') return;
    if ((students?.length || 0) >= 3) return;
    setOpen(true);
    setSport(academy?.sport_type || academy?.subscription?.sport || 'Football');
  }, [academy, students]);

  const dismiss = useCallback(async (completed = false) => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setOpen(false);
    if (completed) {
      try {
        await api.patch('/academy/first-run', { first_run_completed_at: true });
      } catch { /* non-fatal — localStorage already sticks */ }
      toast.success('You\'re all set! Welcome to Out-Play. 🎉');
    }
    onDismiss?.();
  }, [onDismiss]);

  const saveSport = useCallback(async () => {
    setSaving(true);
    try {
      await api.patch('/academy/first-run', { sport_type: sport });
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save sport');
    } finally {
      setSaving(false);
    }
  }, [sport]);

  const addStudent = useCallback(async () => {
    if (!studentForm.name?.trim()) {
      toast.error('Enter the student\'s name');
      return;
    }
    setSaving(true);
    try {
      await api.post('/students', {
        name: studentForm.name.trim(),
        parent_phone: studentForm.parent_phone?.trim() || '',
        parent_contact: studentForm.parent_phone?.trim() || '',
        status: 'active',
      });
      setStudentsAdded(n => n + 1);
      setStudentForm({ name: '', parent_phone: '' });
      toast.success(`Added ${studentForm.name}. ${2 - studentsAdded} more to go 💪`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add student');
    } finally {
      setSaving(false);
    }
  }, [studentForm, studentsAdded]);

  const saveFee = useCallback(async () => {
    const amt = parseFloat(feeAmount);
    if (!amt || amt <= 0) {
      toast.error('Enter a monthly fee amount greater than 0');
      return;
    }
    setSaving(true);
    try {
      await api.patch('/academy/first-run', { default_monthly_fee: amt });
      toast.success(`Monthly fee set to ₹${amt.toLocaleString()}`);
      setStep(4);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save fee');
    } finally {
      setSaving(false);
    }
  }, [feeAmount]);

  const shareQr = useCallback(async () => {
    setSaving(true);
    try {
      // Reuse the existing WA-forward pattern from DashboardSelfSignupQRCard.
      // Fetch the academy's public signup WA link + open wa.me with pre-filled
      // caption so the owner can pick their parents group and forward.
      const res = await api.get('/academy/self-signup-qr?role=student');
      const waLink = res?.data?.wa_link;
      if (waLink) {
        const caption = `Register your child with our academy — tap to open WhatsApp:\n${waLink}\n\nOr scan our signup QR shared with this message.`;
        window.open(`https://wa.me/?text=${encodeURIComponent(caption)}`, '_blank', 'noopener');
      }
      setSharedQr(true);
      toast.success('WhatsApp opened — forward this link to your parents group!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not fetch signup link — you can share it later from the dashboard');
      setSharedQr(true);
    } finally {
      setSaving(false);
    }
  }, []);

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(false); }}>
      <DialogContent className="max-w-lg" data-testid="first-run-wizard">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <DialogTitle>Welcome to Out-Play</DialogTitle>
          </div>
          <p className="text-sm text-muted-foreground">Let&apos;s get your academy running in under 2 minutes.</p>
          <div className="pt-2">
            <Progress value={progressPct} className="h-1.5" />
            <p className="text-[10px] font-mono text-muted-foreground mt-1">Step {step} of {totalSteps}</p>
          </div>
        </DialogHeader>

        {/* ─── Step 1: Sport ─────────────────────────────── */}
        {step === 1 && (
          <div className="space-y-3 py-2" data-testid="wizard-step-1">
            <div className="flex items-center gap-2">
              <Trophy className="h-4 w-4 text-primary" />
              <h3 className="font-semibold">1. Confirm your primary sport</h3>
            </div>
            <p className="text-xs text-muted-foreground">
              This unlocks sport-specific drills, scorecards and templates. You can change it anytime.
            </p>
            <div className="grid grid-cols-3 gap-2">
              {['Football', 'Cricket', 'Basketball', 'Tennis', 'Badminton', 'Swimming', 'Athletics', 'Volleyball', 'Other'].map(s => (
                <button key={s}
                        type="button"
                        onClick={() => setSport(s)}
                        className={`p-2 text-xs rounded-md border text-center transition-colors ${sport === s ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-slate-200 hover:border-primary/50'}`}
                        data-testid={`wizard-sport-${s.toLowerCase()}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ─── Step 2: Add 3 students ────────────────────── */}
        {step === 2 && (
          <div className="space-y-3 py-2" data-testid="wizard-step-2">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              <h3 className="font-semibold">2. Add your first students ({studentsAdded}/3)</h3>
            </div>
            <p className="text-xs text-muted-foreground">
              Quick manual entry now. Later, share the QR poster and parents self-register.
            </p>
            <div className="space-y-2">
              <div>
                <Label htmlFor="wizard-name" className="text-xs">Student name</Label>
                <Input id="wizard-name" value={studentForm.name}
                       onChange={(e) => setStudentForm({ ...studentForm, name: e.target.value })}
                       placeholder="e.g. Arjun Kumar" data-testid="wizard-student-name" />
              </div>
              <div>
                <Label htmlFor="wizard-phone" className="text-xs">Parent&apos;s phone (optional)</Label>
                <Input id="wizard-phone" value={studentForm.parent_phone}
                       onChange={(e) => setStudentForm({ ...studentForm, parent_phone: e.target.value })}
                       placeholder="+919876543210" data-testid="wizard-student-phone" />
              </div>
              <Button onClick={addStudent} disabled={saving || !studentForm.name.trim()}
                      className="w-full" size="sm" data-testid="wizard-add-student-btn">
                {saving ? 'Adding...' : `Add student · ${studentsAdded}/3`}
              </Button>
            </div>
            {studentsAdded > 0 && (
              <div className="flex items-center gap-1 text-xs text-emerald-600">
                <CheckCircle2 className="h-3 w-3" />
                {studentsAdded} added — {studentsAdded >= 3 ? 'you\'re ready to continue' : `${3 - studentsAdded} more (or skip to fees)`}
              </div>
            )}
          </div>
        )}

        {/* ─── Step 3: Fee amount ────────────────────────── */}
        {step === 3 && (
          <div className="space-y-3 py-2" data-testid="wizard-step-3">
            <div className="flex items-center gap-2">
              <IndianRupee className="h-4 w-4 text-primary" />
              <h3 className="font-semibold">3. Set your monthly fee</h3>
            </div>
            <p className="text-xs text-muted-foreground">
              We&apos;ll auto-generate monthly invoices for every student. UPI + Razorpay ready.
            </p>
            <div>
              <Label htmlFor="wizard-fee" className="text-xs">Monthly fee per student (₹)</Label>
              <Input id="wizard-fee" type="number" value={feeAmount}
                     onChange={(e) => setFeeAmount(e.target.value)}
                     placeholder="1500" min={0}
                     data-testid="wizard-fee-input" />
              <p className="text-[10px] text-muted-foreground mt-1">Most Indian academies charge ₹1,000 – ₹3,500 / month.</p>
            </div>
          </div>
        )}

        {/* ─── Step 4: Share QR ──────────────────────────── */}
        {step === 4 && (
          <div className="space-y-3 py-2" data-testid="wizard-step-4">
            <div className="flex items-center gap-2">
              <Share2 className="h-4 w-4 text-primary" />
              <h3 className="font-semibold">4. Fill your roster in 60 seconds</h3>
            </div>
            <p className="text-xs text-muted-foreground">
              Send yourself the parent-onboarding QR poster on WhatsApp. Forward it to your parents group —
              anyone who scans is walked through registration by our bot.
            </p>
            {!sharedQr ? (
              <Button onClick={shareQr} disabled={saving} className="w-full" size="sm"
                      data-testid="wizard-share-qr-btn">
                {saving ? 'Sending...' : 'Send QR poster to my WhatsApp'}
              </Button>
            ) : (
              <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 flex items-center gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                <span className="text-emerald-800">QR poster sent. Forward it to your parents group next.</span>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="pt-2 flex flex-row justify-between gap-2 sm:justify-between">
          <div className="flex gap-2">
            {step > 1 && (
              <Button variant="ghost" size="sm" onClick={() => setStep(step - 1)}
                      data-testid="wizard-back-btn">
                <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => dismiss(false)}
                    className="text-muted-foreground" data-testid="wizard-skip-btn">
              Skip for now
            </Button>
          </div>
          {step === 1 && (
            <Button size="sm" onClick={saveSport} disabled={!sport || saving}
                    data-testid="wizard-next-1">
              {saving ? 'Saving...' : 'Continue'} <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          )}
          {step === 2 && (
            <Button size="sm" onClick={() => setStep(3)}
                    disabled={studentsAdded === 0}
                    data-testid="wizard-next-2">
              Continue <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          )}
          {step === 3 && (
            <Button size="sm" onClick={saveFee} disabled={saving || !feeAmount}
                    data-testid="wizard-next-3">
              {saving ? 'Saving...' : 'Continue'} <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          )}
          {step === 4 && (
            <Button size="sm" onClick={() => dismiss(true)}
                    data-testid="wizard-finish-btn">
              Finish setup <CheckCircle2 className="h-3.5 w-3.5 ml-1" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
